import React, { useState } from 'react';
import HeaderForm from './HeaderForm';
import ObjectiveForm from './ObjectiveForm';
import SkillsForm from './SkillsForm';
import ExperienceForm from './ExperienceForm';
import ProjectsForm from './ProjectsForm';
import EducationForm from './EducationForm';
import CertificationsForm from './CertificationsForm';
import { FiUser, FiTarget, FiTool, FiBriefcase, FiFolder, FiBook, FiAward, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const TabbedResumeForm = ({ formData, setFormData }) => {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    { 
      id: 0, 
      name: 'Personal Info', 
      shortName: 'Info',
      icon: <FiUser />, 
      component: <HeaderForm formData={formData} setFormData={setFormData} />,
      color: '#d4af37'
    },
    { 
      id: 1, 
      name: 'Objective', 
      shortName: 'Goal',
      icon: <FiTarget />, 
      component: <ObjectiveForm formData={formData} setFormData={setFormData} />,
      color: '#c1121f'
    },
    { 
      id: 2, 
      name: 'Skills', 
      shortName: 'Skills',
      icon: <FiTool />, 
      component: <SkillsForm formData={formData} setFormData={setFormData} />,
      color: '#2a9d8f'
    },
    { 
      id: 3, 
      name: 'Experience', 
      shortName: 'Work',
      icon: <FiBriefcase />, 
      component: <ExperienceForm formData={formData} setFormData={setFormData} />,
      color: '#e76f51'
    },
    { 
      id: 4, 
      name: 'Projects', 
      shortName: 'Projects',
      icon: <FiFolder />, 
      component: <ProjectsForm formData={formData} setFormData={setFormData} />,
      color: '#f4a261'
    },
    { 
      id: 5, 
      name: 'Education', 
      shortName: 'Study',
      icon: <FiBook />, 
      component: <EducationForm formData={formData} setFormData={setFormData} />,
      color: '#264653'
    },
    { 
      id: 6, 
      name: 'Certifications', 
      shortName: 'Certs',
      icon: <FiAward />, 
      component: <CertificationsForm formData={formData} setFormData={setFormData} />,
      color: '#8338ec'
    },
  ];

  const currentTab = tabs[activeTab];
  const progress = ((activeTab + 1) / tabs.length) * 100;

  return (
    <div className="editorial-tabs-container">
      {/* Tab Headers - Side Navigation */}
      <div className="tabs-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-title">Sections</div>
          <div className="progress-circle">
            <svg width="60" height="60" viewBox="0 0 60 60">
              <circle
                cx="30"
                cy="30"
                r="26"
                fill="none"
                stroke="rgba(212, 175, 55, 0.2)"
                strokeWidth="4"
              />
              <circle
                cx="30"
                cy="30"
                r="26"
                fill="none"
                stroke="var(--gold-accent)"
                strokeWidth="4"
                strokeDasharray={`${2 * Math.PI * 26}`}
                strokeDashoffset={`${2 * Math.PI * 26 * (1 - progress / 100)}`}
                transform="rotate(-90 30 30)"
                style={{ transition: 'stroke-dashoffset 0.6s ease' }}
              />
            </svg>
            <div className="progress-text">{Math.round(progress)}%</div>
          </div>
        </div>

        <div className="tabs-list">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              style={{
                '--tab-color': tab.color
              }}
            >
              <div className="tab-number">{String(tab.id + 1).padStart(2, '0')}</div>
              <div className="tab-icon">{tab.icon}</div>
              <div className="tab-info">
                <div className="tab-name">{tab.name}</div>
                <div className="tab-short">{tab.shortName}</div>
              </div>
              <div className="tab-indicator"></div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="tabs-content-area">
        {/* Content Header */}
        <div className="content-header">
          <div className="header-decoration" style={{ background: currentTab.color }}></div>
          <div className="header-text">
            <div className="header-section-label">Section {activeTab + 1} of {tabs.length}</div>
            <h2 className="header-title">{currentTab.name}</h2>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content-wrapper">
          <div 
            className="tab-panel"
            key={activeTab} // Force re-render for animation
          >
            {currentTab.component}
          </div>
        </div>

        {/* Navigation Footer */}
        <div className="content-footer">
          <button
            className="nav-button prev"
            onClick={() => setActiveTab(Math.max(0, activeTab - 1))}
            disabled={activeTab === 0}
          >
            <FiChevronLeft />
            <span>Previous</span>
          </button>

          <div className="footer-progress">
            <div className="progress-dots">
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  className={`progress-dot ${activeTab >= tab.id ? 'completed' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                />
              ))}
            </div>
          </div>

          <button
            className="nav-button next"
            onClick={() => setActiveTab(Math.min(tabs.length - 1, activeTab + 1))}
            disabled={activeTab === tabs.length - 1}
          >
            <span>Next</span>
            <FiChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TabbedResumeForm;